// Single source of truth for the conflict-clearance decision vocabulary.
//
// Guide non-residential v2 §5 names the inline decision "Clear / Refer /
// Conflict". The backend stores the long-standing status values unchanged
// (clear / clear_with_conditions / do_not_act) — this is a UI relabel only,
// no data migration. Map in exactly one place so the labels can't drift.
export const CONFLICT_DECISION_LABELS = {
  clear: "Clear",
  clear_with_conditions: "Refer",
  do_not_act: "Conflict",
};

// Issue #2959: the conflict statuses from which POST
// /api/onboarding/send-from-conflict-check will send OB. Mirrors
// `allowed_statuses` in backend/hub/onboarding.py, and a test reads that
// source to keep the two identical. Anything else, notably
// "pending_approval", is refused by the backend with a 400, so the UI must
// not offer the send.
export const OB_SENDABLE_CONFLICT_STATUSES = Object.freeze([
  "clear",
  "clear_with_conditions",
  "na",
  "not_ready",
]);

export function canSendObForConflict(conflictCheck) {
  const cc = conflictCheck || {};
  // The backend lets a director override through whatever the status.
  if (cc.has_override) return true;
  const status = String(cc.conflict_status || "").trim();
  return OB_SENDABLE_CONFLICT_STATUSES.includes(status);
}

export function conflictDecisionLabel(value) {
  if (!value) return "";
  const key = String(value).trim().toLowerCase();
  if (CONFLICT_DECISION_LABELS[key]) return CONFLICT_DECISION_LABELS[key];
  // Unknown/legacy value — humanise rather than swallow.
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Issue #2959 (Vanessa, 24 Sep 2026): a conflict search that only finds the
// client's own earlier records must not go to supervisor approval. The person
// completing the check may confirm a match is the client themselves, fuzzy
// matches included, with a reason. Mirrors backend/hub/own_client_confirmation.py:
// only a client record can be confirmed (another party's record may be a real
// conflict), and the reason needs at least OWN_CLIENT_MIN_REASON characters.
export const OWN_CLIENT_MIN_REASON = 10;

// Same set as _CLIENT_ENTITY_TYPES in backend/hub/same_client_repeat.py.
const CLIENT_ENTITY_TYPES = new Set(["client", "clients", "existing client", "existing_client"]);

export function isClientRecordMatch(match) {
  const et = String((match || {}).entity_type || "").trim().toLowerCase().replace(/\s+/g, " ");
  return CLIENT_ENTITY_TYPES.has(et);
}

// confirmations: { [matchId]: { checked: bool, reason: string } }
export function ownClientConfirmationPayload(confirmations) {
  return Object.entries(confirmations || {})
    .filter(([, c]) => c && c.checked)
    .map(([matchId, c]) => ({ match_id: String(matchId), reason: String(c.reason || "").trim() }));
}

export function ownClientConfirmationsValid(confirmations) {
  return ownClientConfirmationPayload(confirmations).every(
    (c) => c.reason.length >= OWN_CLIENT_MIN_REASON,
  );
}

// True when every match is a client record that has been confirmed as the
// client's own with a valid reason, so the save clears without approval.
export function everyMatchConfirmedOwnClient(matches, confirmations) {
  const list = (matches || []).filter(Boolean);
  if (!list.length) return false;
  return list.every((m) => {
    const c = (confirmations || {})[String(m.id)];
    return (
      isClientRecordMatch(m) &&
      c && c.checked &&
      String(c.reason || "").trim().length >= OWN_CLIENT_MIN_REASON
    );
  });
}

// Issue #2959: a director (the conflict approvers) approving a genuine conflict
// inline must give a reason; mirrors MIN_DIRECTOR_DECISION_REASON_CHARS in
// backend/hub/routes_conflicts.py.
export const DIRECTOR_MIN_REASON = 10;

// BM-H4514 (Vanessa, 19 Oct 2026): a director approving a flagged match says
// which it is. A match the screen cannot confirm as the client's own (here a
// same-name contact record) is often no conflict at all, and was being saved
// as "with conditions" regardless. Nothing is chosen for the director.
export const DIRECTOR_OUTCOMES = Object.freeze({
  no_conflict: "clear",
  with_conditions: "clear_with_conditions",
});

export function directorApprovalStatus(outcome) {
  return DIRECTOR_OUTCOMES[outcome] || null;
}

// Pipeline walkthrough, 24 Sep 2026: the conflict page's Stage 4 button.
// The golden rule puts the FORF after onboarding is RETURNED (complete, or
// overridden with a reason) and AML is approved, never after it is merely
// sent. It needs a matter reference, because the FORF page is keyed on one.
export function forfStageReady({ obDone, thirdfortDone, matterRef }) {
  return Boolean(obDone && thirdfortDone && String(matterRef || "").trim());
}

// The real FORF page (FoRFCombined, reads /api/forf/{ref}/prefill), the one
// every other FORF link in the app uses. Never /new-matter: its pre-fill
// route was lost in the 14 April 2026 wipe (commit 0f21b9d6) and 404s.
export function forfPath(matterRef) {
  return `/forf/${encodeURIComponent(String(matterRef || "").trim())}`;
}
